import React from "react";
import { Card } from "@/components/ui/card";
import TimelineChart from "@/components/TimelineChart";
import { formatDate, formatINR } from "@/lib/constants";

export default function TimelineTab({ client }) {
  const now = new Date();
  const age = (dob) => {
    if (!dob) return null;
    const b = new Date(dob);
    return ((now - b) / (365.25 * 864e5)).toFixed(1);
  };
  const yearsTo = (d) => {
    if (!d) return null;
    return ((new Date(d) - now) / (365.25 * 864e5)).toFixed(1);
  };

  const rows = [];
  if (client.dob) rows.push({ label: "You", now: `${age(client.dob)} yrs`, tone: "primary" });
  if (client.spouse?.dob) rows.push({ label: `Spouse (${client.spouse.name || "—"})`, now: `${age(client.spouse.dob)} yrs`, tone: "accent" });
  (client.children || []).forEach((c) => rows.push({ label: `Child · ${c.name}`, now: `${age(c.dob)} yrs`, tone: "primary" }));
  (client.dependent_parents || []).forEach((p) => rows.push({ label: `Parent · ${p.name}`, now: p.dob ? `${age(p.dob)} yrs` : "—", tone: "muted" }));

  return (
    <div className="space-y-8">
      <Card className="border-border bg-white p-6">
        <h3 className="mb-4 font-dashboard text-base font-bold">Life-cycle visualisation</h3>
        <TimelineChart client={client} />
      </Card>

      <Card className="border-border bg-white p-6">
        <h3 className="mb-4 font-dashboard text-base font-bold">Family life-stage</h3>
        <div className="grid gap-3 md:grid-cols-2">
          {rows.length === 0 && <p className="text-sm text-muted-foreground">Add family details to see the timeline.</p>}
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between rounded-md border border-border bg-white px-4 py-3">
              <div className="text-sm font-medium">{r.label}</div>
              <div className={`text-sm ${r.tone === "accent" ? "text-accent" : "text-primary"} font-semibold`}>{r.now}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="border-border bg-white p-6">
        <h3 className="mb-4 font-dashboard text-base font-bold">Retirement horizon</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-md border border-border bg-secondary/40 p-4">
            <div className="overline text-muted-foreground">Scheduled</div>
            <div className="mt-1 text-lg font-semibold">{formatDate(client.scheduled_retirement_date)}</div>
            <div className="text-xs text-muted-foreground">
              {client.scheduled_retirement_date ? `${yearsTo(client.scheduled_retirement_date)} yrs away` : "—"}
            </div>
          </div>
          <div className="rounded-md border border-primary/20 bg-primary/5 p-4">
            <div className="overline text-primary">Desired</div>
            <div className="mt-1 text-lg font-semibold">{formatDate(client.desired_retirement_date)}</div>
            <div className="text-xs text-muted-foreground">
              {client.desired_retirement_date ? `${yearsTo(client.desired_retirement_date)} yrs away` : "—"}
            </div>
          </div>
        </div>
      </Card>

      <Card className="border-border bg-white p-6">
        <h3 className="mb-4 font-dashboard text-base font-bold">Goals on the horizon</h3>
        <div className="space-y-2">
          {(client.goals || []).length === 0 && <p className="text-sm text-muted-foreground">No goals mapped yet.</p>}
          {(client.goals || []).sort((a, b) => new Date(a.target_date) - new Date(b.target_date)).map((g) => (
            <div key={g.id} className="flex items-center justify-between rounded-md border border-border px-4 py-3">
              <div>
                <div className="font-medium">{g.title}</div>
                <div className="text-xs text-muted-foreground">Target · {formatDate(g.target_date)} · {g.priority}</div>
              </div>
              <div className="text-sm font-semibold text-primary">{formatINR(g.target_amount)}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
